import React from 'react';
import PropTypes from 'prop-types';
import theme from '../../../theme';
import SingleSelect from '../../../components/atoms/forms/SingleSelect';
import Input from '../../../components/atoms/forms/Input';
import { requiresFreeText } from './reasons';

// Controlled reason capture: a SingleSelect over a reason taxonomy plus a
// free-text note that appears once any reason is picked. The note is
// optional for normal reasons and required for "Other" (enforced by
// isReasonComplete in the parent). Selecting a different reason preserves
// whatever note is already typed.
//
// Shared by PinStopModal and RemoveStopsModal, both passing the
// REMOVE_REASONS taxonomy with their own test ids. UpdateRouteModal only
// displays captured reasons, so it uses reasonLabel() from reasons.js
// rather than this picker.

const PLACEHOLDER = '— Select a reason —';

function ReasonPicker({
  options,
  value,
  note,
  onChange,
  reasonTestId,
  noteTestId,
  inputId,
  placeholder,
  disabled,
}) {
  const selectedOption = options.find((r) => r.value === value) || null;
  const isRequired = requiresFreeText(value);

  const handleReasonChange = (opt) => {
    onChange({ value: opt ? opt.value : '', note });
  };

  const handleNoteChange = (e) => {
    onChange({ value, note: e.target.value });
  };

  return (
    <div>
      <SingleSelect
        options={options}
        value={selectedOption}
        onChange={handleReasonChange}
        placeholder={placeholder}
        inputId={inputId}
        isClearable
        isDisabled={disabled}
        data-testid={reasonTestId}
      />
      {value !== '' && (
        <Input
          placeholder={isRequired ? 'Details (required)' : 'Notes (optional)'}
          aria-label={
            isRequired ? 'Reason details (required)' : 'Notes (optional)'
          }
          aria-required={isRequired}
          value={note || ''}
          onChange={handleNoteChange}
          dataTestId={noteTestId}
          style={{ marginTop: theme.spacing.gapMd }}
        />
      )}
    </div>
  );
}

ReasonPicker.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  ).isRequired,
  value: PropTypes.string,
  note: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  reasonTestId: PropTypes.string,
  noteTestId: PropTypes.string,
  inputId: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
};

ReasonPicker.defaultProps = {
  value: '',
  note: '',
  reasonTestId: undefined,
  noteTestId: undefined,
  inputId: undefined,
  placeholder: PLACEHOLDER,
  disabled: false,
};

export default ReasonPicker;
